import type { ModalProps } from "@mantine/core";
import { Modal, Tabs } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useMissingObjectData } from "../../hooks/useMissingObjectData";
import { mergePersonIntoListCache, usePerson } from "../../hooks/usePerson";
import { usePersons } from "../../hooks/usePersons";
import { useSiteCategories } from "../../hooks/useSiteCategories";
import { useSites } from "../../hooks/useSites";
import { TabWithHighlightAndError } from "../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { useToast } from "../../mantine/notifications/useToast";
import type {
  BaseWizardProps,
  NestedSaveProps
} from "../../types/wizard.types";
import type { Person } from "../../utils/backend-types";
import { ObjectName } from "../../utils/enums";
import { buildFieldNameToLabelMap } from "../../utils/react-hook-form/buildFieldNameToLabelMap";
import { HistoryTab } from "../HistoryTab/HistoryTab";
import { useImportPersonFromMastr } from "../ImportOrCreateStep/hooks/useImportPersonFromMastr";
import {
  ImportOrCreateStep,
  PersonMaStRImportSuccessWidget
} from "../ImportOrCreateStep/ImportOrCreateStep";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { usePersonMutations } from "./hooks/usePersonMutations";
import { PersonForm } from "./PersonForm/PersonForm";
import { PERSON_FORM_FIELD_DATA } from "./PersonForm/PersonForm.constants";
import {
  PERSON_WIZARD_TABS,
  PersonWizardTabValue
} from "./PersonWizard.constants";
import "./PersonWizard.scss";

type PersonWizardProps = BaseWizardProps &
  NestedSaveProps<Person> & {
    siteId?: number;
    size?: ModalProps["size"];
    stackId?: string;
    variantId: number;
  };

type WizardStep =
  | { step: "importOrCreate" }
  | { step: "manualCreate" }
  | { step: "importSuccess"; taskId: string };

function PersonWizard({
  graphCoordinates = { x: 0, y: 0 },
  isOpen,
  siteId,
  size = "55rem",
  stackId,
  variantId,
  onClose,
  ...modeProps
}: PersonWizardProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { createPersonMutation, editPersonMutation } = usePersonMutations({
    variantId
  });
  const {
    persons,
    isLoading: isLoadingPersons,
    error: errorPersons
  } = usePersons({ variantId });
  const { projectId } = useParams();
  const { siteCategoriesSummary } = useSiteCategories(projectId, {
    enabled: !!projectId
  });
  const navigate = useNavigate();
  const location = useLocation();
  const {
    data: sites = [],
    isLoading: isLoadingSites,
    error: errorSites
  } = useSites({ variantId });

  const personId = modeProps.mode === "edit" ? modeProps.objectId : undefined;

  const {
    data: person,
    isLoading: isPersonLoading,
    error: personError
  } = usePerson(personId);

  const {
    isLoading: isMissingDataLoading,
    error: missingDataError,
    fieldsThatCouldBeMissing,
    shouldFieldBeHighlighted
  } = useMissingObjectData(personId, siteId);

  const [wizardStep, setWizardStep] = useState<WizardStep>({
    step: "importOrCreate"
  });
  const [numErrors, setNumErrors] = useState(0);
  const [highlightedTabs, setHighlightedTabs] = useState<Array<string>>([]);

  const handleChangeNumErrors = useCallback((numErrors: number) => {
    setNumErrors(numErrors);
  }, []);

  const handleChangeNumHighlightedFields = useCallback(
    (_numHighlightedFields: number, highlightedTabs: Array<string>) => {
      setHighlightedTabs(highlightedTabs);
    },
    []
  );

  function handleChooseManualCreate() {
    setWizardStep({ step: "manualCreate" });
  }

  function handleImportOrCreateSuccess(taskId: string) {
    setWizardStep({ step: "importSuccess", taskId });
  }

  function refreshObjectData() {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "persons"
    });
  }

  function handleNestedMaStRImportResolved(importedPerson: Person) {
    mergePersonIntoListCache(queryClient, { variantId }, importedPerson);
    refreshObjectData();

    if (modeProps.context === "nested") {
      modeProps.onNestedSave(importedPerson);
    }

    onClose();
  }

  function handleSubmitCreate(formData: Person) {
    return createPersonMutation.mutateAsync(
      { newData: formData, graphCoordinates },
      {
        onSuccess: (response) => {
          if (modeProps.context === "nested") {
            modeProps.onNestedSave(response.data);
          } else {
            showToast({
              type: "success",
              message: "Das Unternehmen wurde erstellt."
            });
          }
          onClose();
        }
      }
    );
  }

  function handleSubmitUpdate(formData: Person) {
    return editPersonMutation.mutateAsync(formData, {
      onSuccess: (response) => {
        if (modeProps.context === "nested") {
          modeProps.onNestedSave(response.data);
        } else {
          showToast({
            type: "success",
            message: "Die Änderungen wurden gespeichert."
          });
        }
        onClose();
      }
    });
  }

  function getVisibleTabs() {
    if (modeProps.mode === "edit" && modeProps.context !== "nested") {
      const hasRegulatoryFieldsToShow =
        !siteCategoriesSummary ||
        siteCategoriesSummary.is_eigenerzeugung_plus ||
        person?.smallMediumEnterprise !== null;
      const showRegulatoryDutiesTab =
        person?.shouldRegulatoryDutiesBeChecked && hasRegulatoryFieldsToShow;
      const showTaxFormulaeTab = person?.shouldRegulatoryDutiesBeChecked;
      const showDeliveriesTab =
        person?.isParagraph_6EegActive || person?.isInvoicingActive;

      return PERSON_WIZARD_TABS.filter((tab) => {
        if (tab.value === PersonWizardTabValue.RegulatoryDuties) {
          return showRegulatoryDutiesTab;
        }

        if (tab.value === PersonWizardTabValue.TaxFormulae) {
          return showTaxFormulaeTab;
        }

        if (tab.value === PersonWizardTabValue.Deliveries) {
          return showDeliveriesTab;
        }

        return true;
      });
    }

    return [PERSON_WIZARD_TABS[0]];
  }

  function getActiveTab() {
    if (modeProps.mode === "edit") {
      const lastSegment = location.pathname.split("/").pop() ?? "";
      const visibleTabs = getVisibleTabs();
      const requestedTabIsVisible = visibleTabs.some(
        (tab) => tab.value === lastSegment
      );

      return requestedTabIsVisible ? lastSegment : visibleTabs[0].value;
    }

    return PERSON_WIZARD_TABS[0].value;
  }

  const modalProps = {
    className: "PersonWizard",
    opened: isOpen,
    size,
    stackId,
    onClose
  };

  const activeTab = getActiveTab();
  const showTabs = modeProps.mode === "edit" && modeProps.context !== "nested";
  const visibleTabs = getVisibleTabs();
  const basePath = location.pathname.replace(new RegExp(`/${activeTab}$`), "");
  const fieldNameToLabelMap = buildFieldNameToLabelMap<Person>(
    PERSON_FORM_FIELD_DATA
  );

  function renderFormModal() {
    const action = modeProps.mode === "edit" ? "bearbeiten" : "erstellen";
    const title = person?.name
      ? `${person.name} ${action}`
      : `${ObjectName.Person} ${action}`;
    const isLoading =
      modeProps.mode === "edit" &&
      (isPersonLoading ||
        isMissingDataLoading ||
        isLoadingPersons ||
        isLoadingSites);
    const error =
      modeProps.mode === "edit"
        ? personError || missingDataError || errorPersons || errorSites
        : null;
    const formModeProps =
      modeProps.mode === "create"
        ? { mode: "create" as const }
        : person
          ? {
              mode: "edit" as const,
              wizardObject: person,
              fieldsThatCouldBeMissing,
              shouldFieldBeHighlighted
            }
          : null;

    return (
      <Modal {...modalProps} title={title}>
        <LoadOrError error={error} loading={isLoading}>
          <Tabs
            value={activeTab}
            onChange={(newTab) => {
              if (!newTab) {
                return;
              }

              navigate(`${basePath}/${newTab}`, { state: location.state });
            }}
          >
            {showTabs && (
              <Tabs.List>
                {visibleTabs.map((tab, index) => (
                  <TabWithHighlightAndError
                    component="a"
                    highlighted={highlightedTabs.includes(tab.value)}
                    key={tab.value}
                    numErrors={index === 0 ? numErrors : 0}
                    value={tab.value}
                  >
                    {tab.label}
                  </TabWithHighlightAndError>
                ))}
              </Tabs.List>
            )}
            {formModeProps && (
              <PersonForm
                persons={persons}
                showFooter={activeTab !== PersonWizardTabValue.History}
                siteCategories={siteCategoriesSummary}
                sites={sites}
                variantId={variantId}
                onCancel={onClose}
                onChangeNumErrors={handleChangeNumErrors}
                onChangeNumHighlightedFields={handleChangeNumHighlightedFields}
                onSubmitCreate={handleSubmitCreate}
                onSubmitUpdate={handleSubmitUpdate}
                {...formModeProps}
              />
            )}
            {modeProps.mode === "edit" && modeProps.context !== "nested" && (
              <Tabs.Panel value={PersonWizardTabValue.History}>
                <HistoryTab
                  entityId={modeProps.objectId}
                  entityType={ObjectName.Person}
                  fieldNameToLabelMap={fieldNameToLabelMap}
                />
              </Tabs.Panel>
            )}
          </Tabs>
        </LoadOrError>
      </Modal>
    );
  }

  if (modeProps.mode === "edit") {
    return renderFormModal();
  }

  switch (wizardStep.step) {
    case "importOrCreate":
      return (
        <Modal {...modalProps} title={`${ObjectName.Person} erstellen`}>
          <ImportOrCreateStep
            objectName={ObjectName.Person}
            variantId={variantId}
            onCancel={onClose}
            onChooseManualCreate={handleChooseManualCreate}
            onImportSuccess={handleImportOrCreateSuccess}
          />
        </Modal>
      );
    case "manualCreate":
      return renderFormModal();
    case "importSuccess":
      return (
        <Modal {...modalProps} title={`${ObjectName.Person} erstellen`}>
          {modeProps.context === "nested" ? (
            <NestedMaStRImportResolver
              taskId={wizardStep.taskId}
              onResolved={handleNestedMaStRImportResolved}
            />
          ) : (
            <PersonMaStRImportSuccessWidget
              lastStep
              taskId={wizardStep.taskId}
              onDoneStep={onClose}
              onObjectCreated={refreshObjectData}
            />
          )}
        </Modal>
      );
    default: {
      // Compile-time guard: TypeScript errors here if a new wizardStep variant is added without a matching case above.
      const _exhaustive: never = wizardStep;
      return _exhaustive;
    }
  }
}

type NestedMaStRImportResolverProps = {
  taskId: string;
  onResolved: (person: Person) => void;
};

function NestedMaStRImportResolver({
  taskId,
  onResolved
}: NestedMaStRImportResolverProps) {
  const { data, isLoading, error } = useImportPersonFromMastr(taskId);
  const hasResolvedRef = useRef(false);

  useEffect(() => {
    if (data && !hasResolvedRef.current) {
      hasResolvedRef.current = true;
      onResolved(data);
    }
  }, [data, onResolved]);

  return <LoadOrError error={error} loading={isLoading || !!data} />;
}

export { PersonWizard, PersonWizardProps };
